import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle, faInfoCircle, faExclamationTriangle, faLightbulb } from '@fortawesome/free-solid-svg-icons';
import './Tooltip.css';

/**
 * Tooltip positioning utilities
 */
const calculatePosition = (triggerRect, tooltipRect, placement, offset = 8) => {
  const positions = {
    top: {
      top: triggerRect.top - tooltipRect.height - offset,
      left: triggerRect.left + (triggerRect.width - tooltipRect.width) / 2
    },
    bottom: {
      top: triggerRect.bottom + offset,
      left: triggerRect.left + (triggerRect.width - tooltipRect.width) / 2
    },
    left: {
      top: triggerRect.top + (triggerRect.height - tooltipRect.height) / 2,
      left: triggerRect.left - tooltipRect.width - offset
    },
    right: {
      top: triggerRect.top + (triggerRect.height - tooltipRect.height) / 2,
      left: triggerRect.right + offset
    },
    'top-start': {
      top: triggerRect.top - tooltipRect.height - offset,
      left: triggerRect.left
    },
    'top-end': {
      top: triggerRect.top - tooltipRect.height - offset,
      left: triggerRect.right - tooltipRect.width
    },
    'bottom-start': {
      top: triggerRect.bottom + offset,
      left: triggerRect.left
    },
    'bottom-end': {
      top: triggerRect.bottom + offset,
      left: triggerRect.right - tooltipRect.width
    }
  };

  let pos = positions[placement] || positions.top;

  // Boundary checks and adjustments
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;

  // Horizontal bounds
  if (pos.left < 10) pos.left = 10;
  if (pos.left + tooltipRect.width > viewportWidth - 10) {
    pos.left = viewportWidth - tooltipRect.width - 10;
  }

  // Vertical bounds - flip if needed
  if (pos.top < scrollY + 10 && placement.startsWith('top')) {
    pos.top = triggerRect.bottom + offset;
  }
  if (pos.top + tooltipRect.height > scrollY + viewportHeight - 10 && placement.startsWith('bottom')) {
    pos.top = triggerRect.top - tooltipRect.height - offset;
  }

  return pos;
};

/**
 * Tooltip Component
 * 
 * @param {string} content - Tooltip content (can be string or ReactNode)
 * @param {ReactNode} children - Trigger element
 * @param {string} placement - Tooltip placement (top, bottom, left, right, etc.)
 * @param {number} delay - Delay before showing (ms)
 * @param {string} variant - Visual variant (default, info, warning, success)
 * @param {boolean} arrow - Show arrow pointer
 * @param {boolean} interactive - Allow interaction with tooltip content
 * @param {number} maxWidth - Maximum width in pixels
 */
export const Tooltip = ({
  content,
  children,
  placement = 'top',
  delay = 200,
  variant = 'default',
  arrow = true,
  interactive = false,
  maxWidth = 280,
  disabled = false,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const timeoutRef = useRef(null);

  const showTooltip = useCallback(() => {
    if (disabled) return;
    
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  }, [delay, disabled]);

  const hideTooltip = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  }, []);

  // Update position when visible
  useEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const pos = calculatePosition(triggerRect, tooltipRect, placement);
      setPosition(pos);
    }
  }, [isVisible, placement]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!content) return children;

  return (
    <>
      <span
        ref={triggerRef}
        className="tooltip-trigger"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        {children}
      </span>
      
      {isVisible && (
        <div
          ref={tooltipRef}
          className={`tooltip tooltip-${variant} tooltip-${placement} ${arrow ? 'tooltip-arrow' : ''} ${className}`}
          style={{
            top: position.top,
            left: position.left,
            maxWidth
          }}
          onMouseEnter={interactive ? showTooltip : undefined}
          onMouseLeave={interactive ? hideTooltip : undefined}
          role="tooltip"
        >
          <div className="tooltip-content">
            {content}
          </div>
        </div>
      )}
    </>
  );
};

/**
 * Help Icon with Tooltip
 * A small info icon that shows helpful text on hover
 * 
 * @param {string} text - Help text to display
 * @param {string} icon - Icon type (info, question, warning, tip)
 * @param {string} placement - Tooltip placement
 * @param {string} size - Icon size (sm, md, lg)
 */
export const HelpTooltip = ({
  text,
  icon = 'question',
  placement = 'top',
  size = 'sm',
  variant = 'info',
  className = ''
}) => {
  const icons = {
    question: faQuestionCircle,
    info: faInfoCircle,
    warning: faExclamationTriangle,
    tip: faLightbulb
  };

  const iconSizes = {
    sm: 'xs',
    md: 'sm',
    lg: '1x'
  };

  return (
    <Tooltip content={text} placement={placement} variant={variant}>
      <span className={`help-tooltip-icon help-tooltip-${size} help-tooltip-${icon} ${className}`}>
        <FontAwesomeIcon icon={icons[icon] || icons.question} size={iconSizes[size]} />
      </span>
    </Tooltip>
  );
};

/**
 * Info Badge - Inline info text with tooltip
 */
export const InfoBadge = ({
  label,
  tooltip,
  variant = 'info',
  placement = 'top'
}) => {
  return (
    <Tooltip content={tooltip} placement={placement} variant={variant}>
      <span className={`info-badge info-badge-${variant}`}>
        {label}
        <FontAwesomeIcon icon={faInfoCircle} className="info-badge-icon" />
      </span>
    </Tooltip>
  );
};

/**
 * Contextual help definitions for the application
 * These can be imported and used throughout the app
 */
export const HELP_TEXT = {
  // Token-related help
  tokens: {
    balance: 'Your token balance can be used to redeem rewards and discounts at participating businesses.',
    purchase: 'Top up Doubloons for bonus loyalty perks. Doubloons never expire and unlock sponsored partner rewards across the Guild.',
    redemption: 'To redeem tokens, present your token QR code or member ID at participating businesses.',
  },
  
  // Business listing help
  listings: {
    visibility: 'Control who can see your listing. Public listings are visible to all visitors, while member-only listings require a subscription.',
    categories: 'Select all categories that apply to your business. This helps users find you when searching.',
    hours: 'Set your operating hours. Users will see if you\'re currently open or closed.',
    featured: 'Featured listings appear at the top of search results and on the homepage. Contact us to become featured.',
  },
  
  // Account help
  account: {
    subscription: 'Your subscription level determines what features you can access. Upgrade anytime to unlock more benefits.',
    privacy: 'Control what information is visible to other users on your profile.',
    notifications: 'Choose which notifications you want to receive. You can always change these later.',
  },
  
  // Admin help
  admin: {
    roles: 'User roles determine what actions they can perform. Super Admins have full access, while other roles have specific permissions.',
    audit: 'All admin actions are logged for security. You can review the audit log to see recent changes.',
    alerts: 'Configure which system alerts you want to receive and how you want to be notified.',
  },
  
  // Form field help
  fields: {
    email: 'We\'ll use this email for important account notifications. We never share your email with third parties.',
    phone: 'Optional. Add a phone number if you\'d like businesses to contact you directly.',
    password: 'Use at least 8 characters with a mix of letters, numbers, and symbols for best security.',
    username: 'This will be your display name visible to other users.',
  }
};

/**
 * Form Field with integrated help tooltip
 */
export const FieldWithHelp = ({
  label,
  helpText,
  helpIcon = 'info',
  children,
  required = false,
  error = null
}) => {
  return (
    <div className="field-with-help">
      <div className="field-label-row">
        <label className="field-label">
          {label}
          {required && <span className="field-required">*</span>}
        </label>
        {helpText && (
          <HelpTooltip text={helpText} icon={helpIcon} placement="right" />
        )}
      </div>
      {children}
      {error && <span className="field-error">{error}</span>}
    </div>
  );
};

export default Tooltip;
